import { useNavigate } from "react-router-dom";
import { ServiceRequestsIcon } from "../../../../../assets/icons";
import EmptyState from "../../../../../common/components/xepp/empty-state/EmptyState";
import UtilityPageLayout from "../../../component/layout/UtilityPageLayout";
import { ApplicationRoutes } from "../../../../../routes";
import styles from "./WorkflowsManagmentPage.module.scss";
import { useConfigLayout } from "../../../../../hooks/useConfigLayout";
import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../../../../../common/components/xepp/button/Button";
import { useLazyGetAllWorkflowsQuery } from "../../../../../service/customersupport-service";
import { useDispatch } from "react-redux";
import { resetState } from "../../../../../stores/slices/service-type-slice";
import WorkflowCard from "../components/WorkflowCard/WorkflowCard";
import { WorkflowDefinition } from "../../../../../model/workflows/workflow-definition";
import SearchInput from "../../../../../common/components/xepp/search-input/SearchInput";
import { useDebounce } from "../../../../../hooks/useDebounce";

const PAGE_SIZE = 6;

const WorkflowsManagmentPage = () => {
	const navigator = useNavigate();
	const dispatch = useDispatch();

	const { updateLayout } = useConfigLayout();

	const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const observerTarget = useRef<HTMLDivElement>(null);

	const debouncedSearchTerm = useDebounce(searchTerm, 400);

	const [getWorkflows, { isLoading, isFetching }] = useLazyGetAllWorkflowsQuery();

	useEffect(() => {
		setCurrentPage(1);
		setHasMore(true);
		getWorkflows({
			page: 1,
			pageSize: PAGE_SIZE,
			...(debouncedSearchTerm && { search: debouncedSearchTerm }),
		})
			.unwrap()
			.then(response => {
				setWorkflows(response.items ?? []);
				setHasMore((response?.items?.length ?? 0) === PAGE_SIZE);
			})
			.catch(() => setHasMore(false));
	}, [getWorkflows, debouncedSearchTerm]);

	const loadMore = useCallback(() => {
		if (isFetching || !hasMore) return;
		const nextPage = currentPage + 1;
		setCurrentPage(nextPage);
		getWorkflows({
			page: nextPage,
			pageSize: PAGE_SIZE,
			...(debouncedSearchTerm && { search: debouncedSearchTerm }),
		})
			.unwrap()
			.then(response => {
				const newItems = response.items ?? [];
				setWorkflows(prev => [...prev, ...newItems]);
				setHasMore(newItems.length === PAGE_SIZE);
			})
			.catch(() => setHasMore(false));
	}, [currentPage, isFetching, hasMore, getWorkflows, debouncedSearchTerm]);

	// Infinite scroll observer
	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting && hasMore && !isFetching) {
					loadMore();
				}
			},
			{ threshold: 0.1 }
		);

		const target = observerTarget.current;
		if (target) observer.observe(target);
		return () => {
			if (target) observer.unobserve(target);
		};
	}, [hasMore, isFetching, loadMore]);

	useEffect(() => {
		updateLayout({
			showCountrySelector: false,
			utility: null,
			showExportButton: false,
			showBreadCrumb: false,
		});
	}, [updateLayout]);

	// const workflows: any[] = []

	// Show loader while initial loading
	if (isLoading && workflows.length === 0) {
		return (
			<UtilityPageLayout title="Workflow Managment">
				<div className={styles.loadMoreTrigger}>
					<div className={styles.loadingMore}>
						<div className={styles.spinner} />
						<span>Loading workflows...</span>
					</div>
				</div>
			</UtilityPageLayout>
		);
	}

	return (
		<>
			<UtilityPageLayout
				title="Workflow Managment"
				middleComponent={<SearchInput placeholder="Search workflows" initialValue={searchTerm} onSearch={setSearchTerm} debounceTime={400} />}
				/* EKEDC: Workflow creation disabled - statical SR definitions submitted through UI
				customButton={
					<Button
						text="Create New Workflow"
						variant="secondary"
						onClick={() => {
							dispatch(resetState());
							navigator(`${ApplicationRoutes.NEW}`);
						}}
					/>
				}
				*/
			>
				{workflows.length === 0 && <EmptyState icon={<ServiceRequestsIcon height={50} width={50} />} description="No Workflows Found" />}
				{workflows.length > 0 && (
					<>
						<div className={styles.workflowsContainer}>
							{workflows.map((workflow, index) => (
								<WorkflowCard key={workflow.id ?? index} workflow={workflow} index={index} />
							))}
						</div>
						{hasMore && (
							<div ref={observerTarget} className={styles.loadMoreTrigger}>
								{isFetching && (
									<div className={styles.loadingMore}>
										<div className={styles.spinner} />
										<span>Loading more...</span>
									</div>
								)}
							</div>
						)}
						{!hasMore && workflows.length > 0 && <div className={styles.endOfList}>No more workflows to load</div>}
					</>
				)}
			</UtilityPageLayout>
		</>
	);
};

export default WorkflowsManagmentPage;
